import { marStatusOptions } from '@data'
import { useMedAdministration } from '@hook'
import { Divider, FormButton, FormStep, Modal, SelectInputField, SmallText, TextAreaField, TimeInputField } from '@ui'
import { getError, showToast } from '@util'
import { Box, Form, Text } from 'grommet'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

type Props = {
  onClose: () => void
}

export const AddMAR = ({ onClose }: Props) => {
  const { patientId: patient_id, encounterId: encounter_id } = useParams()
  const { prnMedications, createMedicationAdministration } = useMedAdministration({
    encounter_id,
    patient_id,
    prn: true
  })
  const methods = useForm()
  const [step, setStep] = useState<number>(1)
  const [disableBtn, setDisableBtn] = useState(true)
  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isSubmitting }
  } = methods
  const [btnType, setBtnType] = useState<'button' | 'submit'>('button')

  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => {
        setBtnType('submit')
      }, 500)

      return () => clearTimeout(timer)
    } else {
      setBtnType('button')
    }
  }, [step])

  const medicationWatch = watch('medication')

  const statusWatch = watch('status')
  const administered_at = watch('administered_at')
  const mode = watch('mode')

  const drugs = useMemo(() => {
    if (prnMedications) {
      const data =
        prnMedications.included && prnMedications.included?.length > 0
          ? prnMedications.included.filter(({ type }) => type === 'product')
          : []
      return data
    }
    return []
  }, [prnMedications])

  const routes = useMemo(() => {
    if (prnMedications?.data && prnMedications.data?.length > 0) {
      return prnMedications.data?.map(({ attributes }) => attributes.route)
    }
    return []
  }, [prnMedications])

  const selectedRoutes = useMemo(() => {
    if (prnMedications?.data && prnMedications.data?.length > 0) {
      return prnMedications?.data.find(({ relationships: { product } }) => product.data?.id === medicationWatch)
        ?.attributes.route
    }
  }, [prnMedications, medicationWatch])

  const handleStepChange = () => {
    if (step === 1) setStep(2)
    if (step === 2) setStep(3)
  }

  const medication = useMemo(() => {
    return !!medicationWatch
  }, [medicationWatch])

  const handleClose = () => {
    if (step === 3) return setStep(2)
    if (step === 2) return setStep(1)
    else {
      onClose()
      reset()
    }
  }

  const onSubmit = async (data: any) => {
    const payload = {
      data: {
        attributes: {
          administered_at: data.administered_at,
          mode: data.mode,
          note: data.note,
          status: data.status
        },
        relationships: {
          medication: {
            data: {
              id: `${prnMedications?.data.find(({ relationships: { product } }) => product.data?.id === data.medication)?.id}`,
              type: 'medication'
            }
          },
          patient: {
            data: {
              id: `${patient_id}`,
              type: 'patient'
            }
          }
        },
        type: 'medication_administration'
      }
    }
    try {
      await createMedicationAdministration(payload)
      onClose()
      showToast('M.A.R created successfully')
    } catch (error) {
      getError(error)
    }
  }

  useEffect(() => {
    if (medicationWatch) {
      setValue('mode', selectedRoutes)
    }
    if (step === 1 && medication) {
      setDisableBtn(false)
    } else if (step === 2 && !!statusWatch && (statusWatch === 'administered' ? !!administered_at : true) && !!mode) {
      setDisableBtn(false)
    } else if (step === 3) {
      setDisableBtn(false)
    } else {
      setDisableBtn(true)
    }
  }, [step, medication, statusWatch, administered_at, mode, medicationWatch, setValue, selectedRoutes])
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} reset={reset} title="Add PRN">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box flex={false} overflow="auto" pad={{ horizontal: '32px', vertical: '16px' }} width={'520px'}>
            <Box align="center" direction="row" gap={'16px'} margin={{ bottom: '32px' }}>
              <FormStep check={medication} label="Medication" step={1} />
              <Divider style={{ maxWidth: '48px' }} />
              <FormStep check={!!statusWatch} label="Administration" step={2} />
              <Divider style={{ maxWidth: '48px' }} />
              <FormStep check={step !== undefined && step > 3} label="Notes" step={3} />
            </Box>
            <Box style={{ minHeight: '344px', overflow: 'hidden', position: 'relative' }}>
              <div
                className={`step-slide ${step === 1 ? 'active' : ''}`}
                style={{
                  position: 'absolute',
                  transform: `translateX(${step === 1 ? '0' : '-100%'})`,
                  transition: 'all 0.3s ease-in-out',
                  width: '100%'
                }}
              >
                <Box>
                  <SelectInputField
                    label="Medication"
                    labelKey={arg => arg?.attributes?.generic_name}
                    name="medication"
                    options={drugs}
                    placeholder="Select a medication"
                    valueKey={{ key: 'id', reduce: true }}
                  />
                </Box>
              </div>
              <div
                className={`step-slide ${step === 2 ? 'active' : ''}`}
                style={{
                  position: 'absolute',
                  transform: `translateX(${step === 2 ? '0' : step < 2 ? '100%' : '-100%'})`,
                  transition: 'all 0.3s ease-in-out',
                  width: '100%'
                }}
              >
                <Text color={'#333333'} size="small">
                  Add administration for <SmallText>{getValues('medication')}</SmallText>
                </Text>
                <div className="flex flex-col gap-8 mt-4">
                  <Box>
                    <SelectInputField
                      label="Status"
                      labelKey={'name'}
                      name="status"
                      options={marStatusOptions}
                      placeholder="Select status"
                      valueKey={{ key: 'value', reduce: true }}
                    />
                  </Box>
                  {statusWatch === 'administered' && (
                    <>
                      <Box>
                        <TimeInputField is24HourFormat label="Time" name="administered_at" />
                      </Box>
                      <Box>
                        <SelectInputField
                          disabled
                          dropHeight="300px"
                          label="Mode"
                          name="mode"
                          options={routes}
                          placeholder="Select status"
                        />
                      </Box>
                    </>
                  )}
                </div>
              </div>
              <div
                className={`step-slide ${step === 3 ? 'active' : 'exit-right'}`}
                style={{
                  position: 'absolute',
                  transform: `translateX(${step === 3 ? '0' : '100%'})`,
                  transition: 'all 0.3s ease-in-out',
                  width: '100%'
                }}
              >
                <Box className="">
                  <TextAreaField
                    label="Notes (Optional)"
                    name="note"
                    placeholder="Add notes"
                    style={{ minHeight: '260px' }}
                  />
                </Box>
              </div>
            </Box>
          </Box>
          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Back'}
            disabled={disableBtn || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleStepChange}
            onClose={handleClose}
            submit={step === 3 ? 'Add' : 'Proceed'}
            type={btnType}
          />
        </Form>
      </Modal>
    </FormProvider>
  )
}
